import { memo } from 'react';

import { type ElementInfo } from '~/components/DevModal/types';

type BoxModelDiagramProps = {
  elementInfo: ElementInfo;
};

export const BoxModelDiagram = memo(({ elementInfo }: BoxModelDiagramProps) => {
  return (
    <div
      className="pointer-events-none fixed z-[9999] min-w-[280px] rounded-8 border border-grey-3 bg-white p-4 shadow-xl"
      style={{
        left: (() => {
          const panelWidth = 280;
          const rightPosition = elementInfo.position.x + elementInfo.dimensions.width + 20;
          const leftPosition = elementInfo.position.x - panelWidth - 20;
          const centerPosition = elementInfo.position.x + elementInfo.dimensions.width / 2 - panelWidth / 2;

          // Check if right side fits
          if (rightPosition + panelWidth <= window.innerWidth) {
            return rightPosition;
          }
          // Check if left side fits
          if (leftPosition >= 0) {
            return leftPosition;
          }
          // Check if center fits
          if (centerPosition >= 0 && centerPosition + panelWidth <= window.innerWidth) {
            return centerPosition;
          }
          // Fallback: force it to fit on screen
          return Math.max(10, Math.min(leftPosition, window.innerWidth - panelWidth - 10));
        })(),
        top: (() => {
          // Be more aggressive about avoiding bottom cutoff
          const elementBottom = elementInfo.position.y + elementInfo.dimensions.height;
          const spaceBelow = window.innerHeight - elementBottom;

          // If element is in bottom 1/3 of screen, always show above
          if (elementInfo.position.y > window.innerHeight * 0.66) {
            return Math.max(10, elementInfo.position.y - 300);
          }

          // If there's less than 300px below, show above
          if (spaceBelow < 300) {
            return Math.max(10, elementInfo.position.y - 300);
          }

          // Otherwise show below
          return elementBottom + 20;
        })(),
      }}
    >
      {/* Header */}
      <div className="mb-3 flex items-center justify-between">
        <div className="font-semibold text-14 text-grey-12">Box Model</div>
        <div className="text-12 text-grey-5">
          {elementInfo.tagName}
          {!!elementInfo.id && `#${elementInfo.id}`}
        </div>
      </div>

      {/* Box Model Visualization */}
      <div className="space-y-2">
        {/* Margin */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="size-3 rounded-4 bg-green-5"></div>
            <span className="text-12 text-grey-12">Margin</span>
          </div>
          <span className="font-mono text-12 text-grey-12">
            {elementInfo.styles.marginTop} {elementInfo.styles.marginRight} {elementInfo.styles.marginBottom}{' '}
            {elementInfo.styles.marginLeft}
          </span>
        </div>

        {/* Gap */}
        {(!!elementInfo.styles.gap ||
          !!elementInfo.styles.gridGap ||
          !!elementInfo.styles.columnGap ||
          !!elementInfo.styles.rowGap) && (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="size-3 rounded-4 bg-purple-5"></div>
              <span className="text-12 text-grey-12">Gap</span>
            </div>
            <span className="font-mono text-12 text-grey-12">
              {elementInfo.styles.gap ||
                elementInfo.styles.gridGap ||
                `${elementInfo.styles.columnGap || '0'} × ${elementInfo.styles.rowGap || '0'}`}
            </span>
          </div>
        )}

        {/* Border */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="size-3 rounded-4 bg-red-5"></div>
            <span className="text-12 text-grey-12">Border</span>
          </div>
          <span className="font-mono text-12 text-grey-12">{elementInfo.styles.borderWidth || '0px'}</span>
        </div>

        {/* Padding */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="size-3 rounded-4 bg-orange-5"></div>
            <span className="text-12 text-grey-12">Padding</span>
          </div>
          <span className="font-mono text-12 text-grey-12">
            {elementInfo.styles.paddingTop} {elementInfo.styles.paddingRight} {elementInfo.styles.paddingBottom}{' '}
            {elementInfo.styles.paddingLeft}
          </span>
        </div>

        {/* Content */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="size-3 rounded-4 bg-blue-5"></div>
            <span className="text-12 text-grey-12">Content</span>
          </div>
          <span className="font-mono text-12 text-grey-12">
            {elementInfo.dimensions.width} × {elementInfo.dimensions.height}
          </span>
        </div>
      </div>

      {/* Additional Info */}
      <div className="mt-3 border-grey-2 border-t pt-3">
        <div className="grid grid-cols-2 gap-2 text-12">
          <div>
            <span className="text-grey-12">Position:</span>
            <div className="font-mono text-grey-12">
              {elementInfo.position.x}, {elementInfo.position.y}
            </div>
          </div>
          <div>
            <span className="text-grey-12">Display:</span>
            <div className="font-mono text-grey-12">{elementInfo.styles.display || 'block'}</div>
          </div>
        </div>
      </div>
    </div>
  );
});

BoxModelDiagram.displayName = 'BoxModelDiagram';
